
// Chakra imports
import {
  Avatar,
  Box,
  Button,
  Flex,
  FormControl,
  HStack,
  Input,
  SimpleGrid,
  Spinner,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import { useAuth } from 'contexts/authContext';
import axiosInstance from 'api/axiosinstance';
import authRepository from 'api/authRepository'; 
import { useCallback, useEffect, useRef, useState } from 'react';

const hasValue = (v: string | null | undefined): boolean => {
  if (!v) return false;
  const t = v.trim();
  return !!t && t !== 'null' && t !== 'undefined';
};

export default function Settings() {
  const textColorPrimary = useColorModeValue('secondaryGray.900', 'white');
  const textColorSecondary = 'secondaryGray.600';
  const brandColor = useColorModeValue('#7062B9', '#8A7DD0');
  const brandHoverColor = useColorModeValue('#5B4FA3', '#7062B9');
  const pageBg = useColorModeValue('#F0F2FF', '#0B1437');
  const cardBg = useColorModeValue('white', '#111C44');
  const sectionBg = useColorModeValue('#FAF9FF', '#18224A');
  const clearHoverBg = useColorModeValue('red.50', 'whiteAlpha.100');
  const inputBg = useColorModeValue('white', 'navy.900');
  const inputBorderColor = useColorModeValue('secondaryGray.300', 'whiteAlpha.200');
  const previewBoxBg = useColorModeValue('secondaryGray.100', 'navy.800');

  const { fullName, email, userId, role, setProfilePictureUrl: setAuthProfilePictureUrl } = useAuth();
  const toast = useToast();

  const displayName = fullName || 'No name available';
  const displayEmail = email || 'No email available';

  
  const [phone, setPhone] = useState('');
  const [userRole, setUserRole] = useState('');
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [isSavingPhone, setIsSavingPhone] = useState(false);

  const [profilePictureUrl, setProfilePictureUrlLocal] = useState<string | null>(null);
  const [selectedProfilePicturePreview, setSelectedProfilePicturePreview] = useState<string | null>(null);
  const [profilePictureFile, setProfilePictureFile] = useState<File | null>(null);
  const [isSavingProfilePicture, setIsSavingProfilePicture] = useState(false);
  const profilePictureInputRef = useRef<HTMLInputElement | null>(null);

  const [signaturePreview, setSignaturePreview] = useState<string | null>(null);
  const [uploadedSignaturePreview, setUploadedSignaturePreview] = useState<string | null>(null);
  const [signatureFile, setSignatureFile] = useState<File | null>(null);
  const [signatureLoading, setSignatureLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false); 

  const userRef = useRef<any>(null); 
  const loadedForRef = useRef<string | null>(null); 
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const signatureFileInputRef = useRef<HTMLInputElement | null>(null);
  const isDrawingRef = useRef(false);
  const blobUrlsRef = useRef<string[]>([]);

  const revokeBlobs = () => {
    blobUrlsRef.current.forEach((u) => {
      try {
        URL.revokeObjectURL(u);
      } catch (_e) {
        /* ignore */
      }
    });
    blobUrlsRef.current = [];
  };

  useEffect(() => () => revokeBlobs(), []);

  const loadSignaturePreview = useCallback(
    async (sigValue: string | null | undefined, keepExisting = false) => {
    if (!hasValue(sigValue)) {
      if (!keepExisting) setSignaturePreview(null);
      return;
    }

    revokeBlobs();

    const v = (sigValue as string).trim();

    if (v.startsWith('data:') || v.startsWith('http://') || v.startsWith('https://')) {
      setSignaturePreview(v);
      return;
    }

    setSignatureLoading(true);
    try {
      let blob: Blob;
      try {
        
        blob = await authRepository.downloadFile(v);
      } catch (_first) {
       
        const normalized = v.replace(/\\/g, '/').replace(/^\/+/, '');
        const fileUrl = new URL(normalized, axiosInstance.defaults.baseURL).toString();
        blob = await authRepository.downloadFile(fileUrl);
      }
      const blobUrl = URL.createObjectURL(blob);
      blobUrlsRef.current.push(blobUrl);
      setSignaturePreview(blobUrl);
    } catch (error) {
      console.error('[signature] download failed', error);
      if (!keepExisting) setSignaturePreview(null);
    } finally {
      setSignatureLoading(false);
    }
  },
  []);

  /* Load current user (phone, role, signature, and profile picture) */
  const loadUser = useCallback(async (keepExisting = false) => {
    if (!userId) return;
    try {
      const user: any = await authRepository.getUserById(userId);
      userRef.current = user;

      setPhone(user.phone || '');
      setUserRole(user.role !== undefined && user.role !== null ? String(user.role) : '');

      try {
        const pictureUrl = await authRepository.resolveProfilePicture(user);
        setProfilePictureUrlLocal(pictureUrl);
        setAuthProfilePictureUrl(pictureUrl);
      } catch (error) {
        console.error('Unable to load profile picture:', error);
      }

      const sigKey = Object.keys(user).find(
        (k) => /signature/i.test(k) && typeof user[k] === 'string' && hasValue(user[k])
      );
      const sig = (sigKey ? user[sigKey] : '') as string;
      await loadSignaturePreview(sig, keepExisting);
    } catch (error: any) {
      console.error(error);
      toast({
        title: 'Unable to load profile',
        description: error?.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [userId, loadSignaturePreview, setAuthProfilePictureUrl, toast]);

  useEffect(() => {
    if (!userId || loadedForRef.current === userId) return; 
    loadedForRef.current = userId;
    loadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  /* ---------------- canvas logic ---------------- */
  const prepareCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#000000';
    return { canvas, ctx };
  };

  
  useEffect(() => {
    if (!isEditing) return;

    const timer = window.setTimeout(() => {
      const c = prepareCanvas();
      if (!c) return;
      const { canvas, ctx } = c;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (signaturePreview) {
        const image = new Image();
        image.onload = () => ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
        image.onerror = () => console.error('Failed to draw existing signature on canvas');
        image.src = signaturePreview;
      }
    }, 50);

    return () => window.clearTimeout(timer);
   
  }, [isEditing]);

  const getCoords = (
    e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
  ) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();

    let clientX = 0;
    let clientY = 0;
    if ('touches' in e) {
      if (!e.touches.length) return null;
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    return {
      x: ((clientX - rect.left) / rect.width) * canvas.width,
      y: ((clientY - rect.top) / rect.height) * canvas.height,
    };
  };

  const startDrawing = (
    e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
  ) => {
    e.preventDefault();
    const c = prepareCanvas();
    const pos = getCoords(e);
    if (!c || !pos) return;

    c.ctx.beginPath();
    c.ctx.moveTo(pos.x, pos.y);
    isDrawingRef.current = true;
  };

  const drawSignature = (
    e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
  ) => {
    if (!isDrawingRef.current) return;
    e.preventDefault();
    const c = prepareCanvas();
    const pos = getCoords(e);
    if (!c || !pos) return;

    c.ctx.lineTo(pos.x, pos.y);
    c.ctx.stroke();
  };

  const stopDrawing = () => {
    isDrawingRef.current = false;
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const cancelEditing = () => {
    isDrawingRef.current = false;
    setSignatureFile(null);
    setUploadedSignaturePreview(null);
    setIsEditing(false);
  };

  const handleSignatureFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Please select an image file.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      event.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result !== 'string') {
        toast({
          title: 'Unable to preview signature image.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      setSignatureFile(file);
      setUploadedSignaturePreview(reader.result);
    };
    reader.onerror = () => {
      toast({
        title: 'Unable to read signature image.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    };
    reader.readAsDataURL(file);
  };

  const handleProfilePictureChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Please select an image file.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      event.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result !== 'string') {
        toast({
          title: 'Unable to preview profile picture.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      setProfilePictureFile(file);
      setSelectedProfilePicturePreview(reader.result);
    };
    reader.onerror = () => {
      toast({
        title: 'Unable to read profile picture.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    };
    reader.readAsDataURL(file);
  };

  const cancelProfilePictureChange = () => {
    setProfilePictureFile(null);
    setSelectedProfilePicturePreview(null);
    if (profilePictureInputRef.current) profilePictureInputRef.current.value = '';
  };

  const saveProfilePicture = async () => {
    if (!userId || !profilePictureFile) return;
    const currentUser = userRef.current;
    if (!currentUser) {
      toast({
        title: 'Profile is still loading. Please try again.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsSavingProfilePicture(true);
      const updatedUser = await authRepository.updateUser(
        userId,
        currentUser,
        profilePictureFile
      );
      userRef.current = { ...currentUser, ...updatedUser };
      setProfilePictureUrlLocal(selectedProfilePicturePreview);
      setAuthProfilePictureUrl(selectedProfilePicturePreview);
      cancelProfilePictureChange();
      toast({
        title: 'Profile picture saved successfully.',
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: 'Unable to save profile picture',
        description: error?.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSavingProfilePicture(false);
    }
  };

  const saveSignature = async () => {
    const canvas = canvasRef.current;
    if (!canvas && !signatureFile) return;

    if (!userId) {
      toast({
        title: 'User ID not found. Please log in again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const dataUrl = uploadedSignaturePreview || canvas?.toDataURL('image/png');
    if (!dataUrl) return;

    try {
      setIsSaving(true);
      await authRepository.saveESignature(userId, dataUrl, userRef.current, signatureFile ?? undefined);

      setSignaturePreview(dataUrl); 
      setSignatureFile(null);
      setUploadedSignaturePreview(null);
      setIsEditing(false);

      toast({
        title: 'Signature saved successfully.',
        status: 'success',
        duration: 2500,
        isClosable: true,
      });

    } catch (error: any) {
      toast({
        title: 'Unable to save signature',
        description: error?.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const savePhone = async () => {
    if (!userId) {
      toast({
        title: 'User ID not found. Please log in again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const updatedPhone = phone.trim();
    if (!updatedPhone) {
      toast({
        title: 'Please enter a phone number.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const currentUser = userRef.current;
    if (!currentUser) {
      toast({
        title: 'Profile is still loading. Please try again.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsSavingPhone(true);
      const updatedUser = await authRepository.updateUser(userId, {
        ...currentUser,
        phone: updatedPhone,
      });
      userRef.current = { ...currentUser, ...updatedUser };
      setPhone(updatedUser.phone || updatedPhone);
      setIsEditingPhone(false);
      toast({
        title: 'Phone number saved successfully.',
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: 'Unable to save phone number',
        description: error?.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSavingPhone(false);
    }
  };

  /* ---------------- helpers ---------------- */
  const displayRole = role || userRole || '-';
  const displayPhone = phone || '-';

  const readOnlyField = (label: string, value: string, span2 = false) => (
    <Box gridColumn={span2 ? { base: 'span 1', md: 'span 2' } : undefined}>
      <Text fontSize="sm" fontWeight="600" mb="8px" color={textColorPrimary}>
        {label}
      </Text>
      <Input
        value={value}
        isReadOnly
        variant="filled"
        bg={inputBg}
        color={textColorPrimary}
        border="1px solid"
        borderColor={inputBorderColor}
      />
    </Box>
  );

  /* ---------------- UI ---------------- */
  return (
    <Box mt="70px" p={2} bg={pageBg} borderRadius="25px">
      <Card
        bg={cardBg}
        borderRadius="25px"
        overflow="hidden"
        boxShadow="0 10px 30px rgba(112,98,185,0.12)"
        w="100%"
        p={{ base: 4, md: 6 }}
      >
      <SimpleGrid columns={{ base: 1, xl: 2 }} spacing="20px">
        {/* PROFILE DETAILS */}
        <Box p="24px" bg={sectionBg} borderRadius="16px">
          <Flex direction="column" mb="30px">
            <Text fontSize="2xl" color={textColorPrimary} fontWeight="bold">
              Profile Details
            </Text>
            <Text fontSize="md" color={textColorSecondary}>
              Your account information is read-only
            </Text>
          </Flex>

          <Flex
            align="center"
            gap={4}
            mb="24px"
            p="16px"
            border="1px solid"
            borderColor={inputBorderColor}
            borderRadius="14px"
            bg={inputBg}
            flexWrap="wrap"
          >
            <Avatar
              size="xl"
              name={displayName}
              src={selectedProfilePicturePreview || profilePictureUrl || undefined}
            />
            <Flex direction="column" gap={2}>
              <Text fontWeight="600" color={textColorPrimary}>
                Profile Picture
              </Text>
              <Text fontSize="sm" color={textColorSecondary}>
                Upload an image to replace the initials in the top-right profile icon.
              </Text>
              <Input
                ref={profilePictureInputRef}
                type="file"
                accept="image/*"
                onChange={handleProfilePictureChange}
                display="none"
              />
              <HStack flexWrap="wrap">
                <Button
                  variant="outline"
                  borderColor={inputBorderColor}
                  color={textColorPrimary}
                  onClick={() => profilePictureInputRef.current?.click()}
                  isDisabled={isSavingProfilePicture}
                >
                  {profilePictureFile ? 'Choose another image' : 'Choose image'}
                </Button>
                {profilePictureFile && (
                  <>
                    <Button
                      bg={brandColor}
                      color="white"
                      onClick={saveProfilePicture}
                      isLoading={isSavingProfilePicture}
                      _hover={{ bg: brandHoverColor }}
                    >
                      Save picture
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={cancelProfilePictureChange}
                      isDisabled={isSavingProfilePicture}
                    >
                      Cancel
                    </Button>
                  </>
                )}
              </HStack>
            </Flex>
          </Flex>

          <FormControl>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px">
              {readOnlyField('Name', displayName)}
              {readOnlyField('Role', displayRole)}
              {readOnlyField('Email', displayEmail, true)}
              <Box gridColumn={{ base: 'span 1', md: 'span 2' }}>
                <Text fontSize="sm" fontWeight="600" mb="8px" color={textColorPrimary}>
                  Contact Number
                </Text>
                <HStack align="flex-start" spacing={3} flexWrap="wrap">
                  <Input
                    type="tel"
                    value={isEditingPhone ? phone : displayPhone}
                    isReadOnly={!isEditingPhone}
                    variant={isEditingPhone ? 'outline' : 'filled'}
                    bg={inputBg}
                    color={textColorPrimary}
                    border="1px solid"
                    borderColor={inputBorderColor}
                    onChange={(event) => setPhone(event.target.value)}
                    placeholder="Enter contact number"
                    flex="1"
                    minW="200px"
                  />
                  {isEditingPhone ? (
                    <>
                      <Button
                        h="50px"
                        px={6}
                        variant="outline"
                        borderColor={inputBorderColor}
                        color={textColorPrimary}
                        borderRadius="14px"
                        onClick={() => {
                          setPhone(userRef.current?.phone || '');
                          setIsEditingPhone(false);
                        }}
                        isDisabled={isSavingPhone}
                      >
                        Cancel
                      </Button>
                      <Button
                        h="50px"
                        px={7}
                        bg={brandColor}
                        color="white"
                        borderRadius="14px"
                        onClick={savePhone}
                        isLoading={isSavingPhone}
                        _hover={{ bg: brandHoverColor }}
                      >
                        Save
                      </Button>
                    </>
                  ) : (
                    <Button
                      h="50px"
                      px={7}
                      bg={brandColor}
                      color="white"
                      borderRadius="14px"
                      onClick={() => setIsEditingPhone(true)}
                      _hover={{ bg: brandHoverColor }}
                    >
                      {phone ? 'Edit' : 'Add Phone Number'}
                    </Button>
                  )}
                </HStack>
              </Box>
            </SimpleGrid>
          </FormControl>
        </Box>

        {/* E-SIGNATURE */}
        <Box p="24px" bg={sectionBg} borderRadius="16px">
          <Flex direction="column" mb="20px">
            <Text fontSize="2xl" color={textColorPrimary} fontWeight="bold">
              E-Signature
            </Text>
            <Text fontSize="md" color={textColorSecondary}>
              Draw your signature for reference and approval records
            </Text>
          </Flex>

          <Flex
            direction="column"
            align="center"
            justify="center"
            border="1px dashed"
            borderColor={inputBorderColor}
            borderRadius="16px"
            p="20px"
            bg={inputBg}
          >
            {/* ---------- preview (always visible) ---------- */}
            <Text fontSize="xs" fontWeight="600" color={textColorSecondary} mb={2}>
              Signature Preview
            </Text>

            <Box
              w="260px"
              h="100px"
              borderRadius="12px"
              bg={uploadedSignaturePreview || signaturePreview ? 'white' : previewBoxBg}
              display="flex"
              alignItems="center"
              justifyContent="center"
              overflow="hidden"
              border="1px solid"
              borderColor={inputBorderColor}
              color={brandColor}
              fontSize="38px"
              fontWeight="700"
            >
              {signatureLoading ? (
                <HStack spacing={2}>
                  <Spinner size="sm" />
                  <Text fontSize="xs" color="gray.500">
                    Loading...
                  </Text>
                </HStack>
              ) : uploadedSignaturePreview || signaturePreview ? (
                <img
                  src={uploadedSignaturePreview || signaturePreview}
                  alt="E-Signature"
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  onError={() => setSignaturePreview(null)}
                />
              ) : (
                '✍️'
              )}
            </Box>

            <Text fontSize="sm" color={textColorSecondary} textAlign="center" mt="12px">
              {uploadedSignaturePreview ? 'Image selected. Save to upload.' : signaturePreview ? 'Signature saved.' : 'No signature added yet.'}
            </Text>

            {isEditing ? (
              /* ---------- inline signature pad (no drawer / modal) ---------- */
              <Flex direction="column" align="center" w="100%" maxW="520px" mt={5}>
                <Text fontSize="sm" fontWeight="600" color={textColorPrimary} mb={1}>
                  Sign inside the box
                </Text>
                <Text fontSize="xs" color="gray.500" mb={3} textAlign="center">
                  Draw your signature or upload an image.
                </Text>
                <Input
                  ref={signatureFileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleSignatureFileChange}
                  display="none"
                />
                <Button
                  mb={3}
                  variant="outline"
                  borderColor={inputBorderColor}
                  color={textColorPrimary}
                  borderRadius="14px"
                  onClick={() => signatureFileInputRef.current?.click()}
                  isDisabled={isSaving}
                >
                  {signatureFile ? signatureFile.name : 'Upload Signature Image'}
                </Button>

                <Box
                  border="2px dashed"
                  borderColor="blue.300"
                  borderRadius="14px"
                  bg="white"
                  overflow="hidden"
                  position="relative"
                  width="100%"
                  height="220px"
                >
                  <canvas
                    ref={canvasRef}
                    width={700}
                    height={260}
                    style={{
                      width: '100%',
                      height: '100%',
                      display: 'block',
                      cursor: 'crosshair',
                      touchAction: 'none',
                    }}
                    onMouseDown={startDrawing}
                    onMouseMove={drawSignature}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={drawSignature}
                    onTouchEnd={stopDrawing}
                    onTouchCancel={stopDrawing}
                  />

                  <Text
                    position="absolute"
                    bottom="10px"
                    left="0"
                    right="0"
                    textAlign="center"
                    fontSize="xs"
                    color="gray.400"
                    pointerEvents="none"
                  >
                    Sign here
                  </Text>
                </Box>

                <HStack spacing={3} mt={4} flexWrap="wrap" justify="center">
                  <Button
                    h="50px"
                    px={6}
                    variant="outline"
                    borderColor="red.400"
                    color="red.500"
                    borderRadius="14px"
                    onClick={clearSignature}
                    isDisabled={isSaving}
                    _hover={{ bg: clearHoverBg }}
                  >
                    Clear
                  </Button>
                  <Button
                    h="50px"
                    px={6}
                    variant="outline"
                    borderColor={inputBorderColor}
                    color={textColorPrimary}
                    borderRadius="14px"
                    onClick={cancelEditing}
                    isDisabled={isSaving}
                  >
                    Cancel
                  </Button>
                  <Button
                    h="50px"
                    px={7}
                    bg={brandColor}
                    color="white"
                    borderRadius="14px"
                    onClick={saveSignature}
                    isLoading={isSaving}
                    _hover={{ bg: brandHoverColor }}
                  >
                    Save Signature
                  </Button>
                </HStack>
              </Flex>
            ) : (
              <Button
                mt={4}
                h="50px"
                px={7}
                bg={brandColor}
                color="white"
                borderRadius="14px"
                onClick={() => setIsEditing(true)}
                _hover={{ bg: brandHoverColor }}
              >
                {signaturePreview ? 'Edit Signature' : 'Add Signature'}
              </Button>
            )}
          </Flex>
        </Box>
      </SimpleGrid>
      </Card>
    </Box>
  );
}